import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { RedirectToSignIn, useUser } from '@clerk/clerk-react';
import { useConfig } from '@/hooks/useConfig';
import { getActiveTemplate } from '@/hooks/useSiteConfig';

// Layouts
import PublicLayout from '@/layouts/PublicLayout';
import AdminLayout from '@/layouts/AdminLayout';

// Public Pages
import HomePage from '@/pages/HomePage';

// Demo Pages (these wrap templates with sample data)
import CuratorDemo from '@/pages/demos/CuratorDemo';
import TraderDemo from '@/pages/demos/TraderDemo';
import ResearcherDemo from '@/pages/demos/ResearcherDemo';
import OnboarderDemo from '@/pages/demos/OnboarderDemo';
import EcosystemDemo from '@/pages/demos/EcosystemDemo';
import IntellectualDemo from '@/pages/demos/IntellectualDemo';
import StartHereDemo from '@/pages/demos/StartHereDemo';

// Admin Pages
import Dashboard from '@/pages/admin/Dashboard';
import ContentEditor from '@/pages/admin/ContentEditor';
import LeadsInbox from '@/pages/admin/LeadsInbox';

// Template Registry - using Demo pages which wrap templates with sample data
const TEMPLATES: Record<string, React.ComponentType> = {
    curator: CuratorDemo,
    trader: TraderDemo,
    researcher: ResearcherDemo,
    onboarder: OnboarderDemo,
    ecosystem: EcosystemDemo,
    intellectual: IntellectualDemo,
    degen: HomePage, // Default template
};

// Get active template component
function ActiveTemplatePage(): JSX.Element {
    const templateKey = getActiveTemplate();
    const TemplateComponent = TEMPLATES[templateKey] || HomePage;
    return <TemplateComponent />;
}

// Loading skeleton
function LoadingScreen() {
    return (
        <div className="min-h-screen bg-void flex items-center justify-center">
            <div className="text-center">
                <div className="w-12 h-12 border-2 border-neon-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-zinc-500 font-mono text-sm">INITIALIZING...</p>
            </div>
        </div>
    );
}

// Protected route wrapper with optional email gating
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const clerkEnabled = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
    const { user, isLoaded } = useUser();
    const adminEmailsEnv = import.meta.env.VITE_ADMIN_EMAILS || '';
    const allowedEmails = adminEmailsEnv.split(',').map((e: string) => e.trim().toLowerCase()).filter(Boolean);

    if (!clerkEnabled) {
        // Dev mode: allow access without auth if Clerk is disabled
        return <>{children}</>;
    }

    // Wait for Clerk to load
    if (!isLoaded) {
        return <LoadingScreen />;
    }

    // Check if user is signed in
    if (!user) {
        return <RedirectToSignIn />;
    }

    // If allowlist is defined, check if user's email is allowed
    const userEmail = user.primaryEmailAddress?.emailAddress.toLowerCase();

    // FAIL CLOSED: If allowlist is configured but user is not in it -> Deny
    // FAIL CLOSED: If allowlist is NOT configured -> Deny (Security precaution)
    // Actually, for ease of setup, if allowlist is EMPTY, we might want to default to closed or open?
    // Let's go with: If VITE_ADMIN_EMAILS is set, enforce it. If NOT set, block everyone to be safe.

    const isAllowed = allowedEmails.length > 0 && userEmail && allowedEmails.includes(userEmail);

    if (!isAllowed) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
                <div className="text-red-500 font-mono mb-4 text-center">
                    ACCESS DENIED
                </div>
                <p className="text-zinc-500 mb-6 text-center max-w-md">
                    Your email ({userEmail}) is not authorized to access the admin panel.
                </p>
                <a
                    href="/"
                    className="px-6 py-2 bg-zinc-800 text-white font-bold rounded hover:bg-zinc-700 transition-colors"
                >
                    Return Home
                </a>
            </div>
        );
    }

    return <>{children}</>;
}

function App() {
    // Skip config loading for demo routes (they are standalone)
    const isDemoRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/demo');
    const { isLoading } = useConfig();

    // Don't block demo routes on config loading
    if (isLoading && !isDemoRoute) {
        return <LoadingScreen />;
    }

    return (
        <BrowserRouter>
            <Routes>
                {/* Public Zone - Renders Active Template */}
                <Route path="/" element={<PublicLayout />}>
                    <Route index element={<ActiveTemplatePage />} />
                </Route>
                <Route path="/start" element={<StartHereDemo />} />

                {/* Template Demos */}
                <Route path="/demo/curator" element={<CuratorDemo />} />
                <Route path="/demo/trader" element={<TraderDemo />} />
                <Route path="/demo/researcher" element={<ResearcherDemo />} />
                <Route path="/demo/onboarder" element={<OnboarderDemo />} />
                <Route path="/demo/ecosystem" element={<EcosystemDemo />} />
                <Route path="/demo/intellectual" element={<IntellectualDemo />} />

                {/* Admin Zone (Protected) */}
                <Route
                    path="/admin/*"
                    element={
                        <ProtectedRoute>
                            <AdminLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Dashboard />} />
                    <Route path="content" element={<ContentEditor />} />
                    <Route path="leads" element={<LeadsInbox />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default App;

